/**
 * ContextSelector — adaptive selector for lists of any length.
 *
 * <5 options  → chip row (inline, always visible)
 * ≥5 options  → trigger button + bottom sheet with search
 *
 * Props:
 *   label       — optional label above the selector
 *   value       — current selected id (or null)
 *   options     — [{id, label, sublabel?, category?, icon?}]
 *   onChange    — (id | null) => void
 *   placeholder — text when nothing selected (default: 'Auswählen...')
 *   allowClear  — show "Alle" / clear option (default: false)
 *   disabled    — disable all interactions (default: false)
 *
 * See: docs/architecture/UX_NAVIGATION_PATTERNS_V2.md §13
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from './overlay';

export default function ContextSelector({
  label,
  value,
  options = [],
  onChange,
  placeholder = 'Auswählen...',
  allowClear = false,
  disabled = false,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const useSheet = options.length >= 5;

  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const current = options.find(o => o.id === value);

  // ── Chip row (< 5 options) ─────────────────────────────────────────────────
  if (!useSheet) {
    return (
      <div data-testid="context-selector-chips">
        {label && <div className="text-xs text-[var(--aif-text-muted)] mb-1">{label}</div>}
        <div className="flex gap-2 flex-wrap">
          {allowClear && (
            <button
              onClick={() => !disabled && onChange(null)}
              disabled={disabled}
              className={`px-3 py-2 rounded-full text-sm min-h-[44px] transition
                ${!value
                  ? 'bg-[var(--aif-color-primary)] text-white'
                  : 'bg-[var(--aif-bg-elevated)] text-[var(--aif-text-secondary)]'
                } disabled:opacity-50`}
              data-testid="context-selector-clear"
            >
              Alle
            </button>
          )}
          {options.map(opt => (
            <button
              key={opt.id}
              onClick={() => !disabled && onChange(opt.id)}
              disabled={disabled}
              className={`px-3 py-2 rounded-full text-sm min-h-[44px] transition flex items-center gap-1
                ${value === opt.id
                  ? 'bg-[var(--aif-color-primary)] text-white'
                  : 'bg-[var(--aif-bg-elevated)] text-[var(--aif-text-secondary)]'
                } disabled:opacity-50`}
              data-testid={`context-selector-chip-${opt.id}`}
            >
              {opt.icon && <span>{opt.icon}</span>}
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Trigger + Bottom sheet (≥ 5 options) ───────────────────────────────────
  return (
    <>
      {label && <div className="text-xs text-[var(--aif-text-muted)] mb-1">{label}</div>}
      <button
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
        className="w-full min-h-[44px] px-4 py-2 rounded-xl bg-[var(--aif-bg-elevated)]
                   border border-[var(--aif-border-default)] text-left flex items-center justify-between
                   hover:border-[var(--aif-color-primary)] transition disabled:opacity-50"
        data-testid="context-selector-trigger"
      >
        <span className="text-sm text-[var(--aif-text-primary)] truncate">
          {current ? (
            <span className="flex items-center gap-1">
              {current.icon && <span>{current.icon}</span>}
              {current.label}
            </span>
          ) : (
            <span className="text-[var(--aif-text-muted)]">{placeholder}</span>
          )}
        </span>
        <span className="text-[var(--aif-text-muted)] flex-shrink-0 ml-2">▾</span>
      </button>

      <Sheet.Root
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            setOpen(false);
            setQuery('');
          }
        }}
      >
        <Sheet.Content side="bottom" size="md" title={label}>
          <Sheet.Body>
            {/* Search */}
            <input
              type="text"
              autoFocus
              placeholder={t('ui.search')}
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full p-3 rounded-xl bg-[var(--aif-bg-elevated)]
                         border border-[var(--aif-border-default)] text-[var(--aif-text-primary)]
                         focus:outline-none focus:border-[var(--aif-color-primary)]
                         mb-3 min-h-[44px] text-sm"
              data-testid="context-selector-search"
            />

            <div className="space-y-1">
              {/* Clear option */}
              {allowClear && (
                <button
                  onClick={() => { onChange(null); setOpen(false); setQuery(''); }}
                  className={`w-full min-h-[44px] px-3 py-2.5 text-left rounded-xl text-sm transition
                    ${!value
                      ? 'bg-[var(--aif-color-primary)] text-white'
                      : 'text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-elevated)]'
                    }`}
                  data-testid="context-selector-sheet-clear"
                >
                  Alle
                </button>
              )}

              {/* Options */}
              {filtered.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => { onChange(opt.id); setOpen(false); setQuery(''); }}
                  className={`w-full min-h-[44px] px-3 py-2.5 text-left rounded-xl transition
                    ${value === opt.id
                      ? 'bg-[var(--aif-color-primary)] text-white'
                      : 'hover:bg-[var(--aif-bg-elevated)]'
                    }`}
                  data-testid={`context-selector-option-${opt.id}`}
                >
                  <div className="flex items-center gap-2">
                    {opt.icon && <span className="flex-shrink-0">{opt.icon}</span>}
                    <div className="min-w-0">
                      <div className={`text-sm ${value === opt.id ? 'text-white font-medium' : 'text-[var(--aif-text-primary)]'}`}>
                        {opt.label}
                      </div>
                      {opt.sublabel && (
                        <div className={`text-xs truncate ${value === opt.id ? 'text-white/80' : 'text-[var(--aif-text-muted)]'}`}>
                          {opt.sublabel}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}

              {/* Empty state */}
              {filtered.length === 0 && (
                <div
                  className="text-center text-[var(--aif-text-muted)] py-8 text-sm"
                  data-testid="context-selector-empty"
                >
                  Keine Ergebnisse
                </div>
              )}
            </div>
          </Sheet.Body>
        </Sheet.Content>
      </Sheet.Root>
    </>
  );
}
